import React, { useContext, useState, useEffect } from "react";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";
import { themeContext } from "../Context";

const SharePopup = ({ question, onClose, language: parentLanguage }) => {
    const theme = useContext(themeContext);
    const darkMode = theme.state.darkMode;

    // Use parent language if provided, otherwise default to 'en'
    const language = parentLanguage || 'en';

    const [selectedOption, setSelectedOption] = useState(null);

    // Get localized question text based on language
    const getLocalizedText = () => {
        if (!question) return { question: '', options: [] };

        if (language === 'en') {
            return {
                question: question.question,
                options: question.options
            };
        }

        if (language === 'hi' && question.questionHi) {
            return {
                question: question.questionHi,
                options: question.optionsHi || question.options // Fallback to English options if Hindi not available
            };
        }

        if (language === 'bilingual' && question.questionHi) {
            return {
                question: `${question.question}\n\n${question.questionHi}`,
                options: question.options.map((enOpt, idx) => {
                    const hiOpt = question.optionsHi?.[idx];
                    return hiOpt ? `${enOpt} / ${hiOpt}` : enOpt;
                })
            };
        }

        // Fallback to English
        return {
            question: question.question,
            options: question.options
        };
    };

    // Get language display name for UI
    const getLanguageDisplay = () => {
        switch (language) {
            case 'hi': return 'हिंदी';
            case 'bilingual': return 'Bilingual';
            default: return 'English';
        }
    };

    // Prevent scrolling on the background
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    const handleOptionClick = (questionId, option) => {
        setSelectedOption(option);
    };

    const getOptionStyle = (selectedOption, correctAnswer, option) => {
        if (option === correctAnswer && selectedOption === option) {
            return {
                backgroundColor: darkMode ? '#1e4a2a' : '#d4edda',
                border: darkMode ? '1px solid #2d6a4f' : '1px solid #28a745',
                color: darkMode ? '#ffffff' : '#155724',
                fontWeight: '600',
                boxShadow: darkMode ? '0 4px 8px rgba(0,255,0,0.1)' : '0 4px 8px rgba(40,167,69,0.2)'
            };
        }
        if (option === selectedOption && option !== correctAnswer) {
            return {
                backgroundColor: darkMode ? '#5a2a2a' : '#f8d7da',
                border: darkMode ? '1px solid #b02a37' : '1px solid #dc3545',
                color: darkMode ? '#ffffff' : '#721c24',
                fontWeight: '600',
                boxShadow: darkMode ? '0 4px 8px rgba(255,0,0,0.1)' : '0 4px 8px rgba(220,53,69,0.2)'
            };
        }
        return {
            backgroundColor: darkMode ? '#2d2d2d' : '#ffffff',
            border: darkMode ? '1px solid #444' : '1px solid #e0e0e0',
            transition: 'all 0.2s ease'
        };
    };

    const handleDownload = () => {
        const element = document.getElementById(`share-question-${question.id}`);
        if (!element) {
            console.error("Element not found");
            return;
        }

        const { width, height } = element.getBoundingClientRect();
        const aspectRatio = width / height;
        const canvasWidth = 400;
        const canvasHeight = canvasWidth / aspectRatio;

        toPng(element, {
            quality: 2,
            canvasWidth,
            canvasHeight,
        })
            .then((dataUrl) => {
                saveAs(dataUrl, `question-${question.id}.png`);
            })
            .catch((err) => console.error("Error generating image:", err));
    };

    const handleCopyToClipboard = async () => {
        if (!navigator.clipboard || !window.ClipboardItem) {
            alert("Clipboard API is not supported in this browser.");
            return;
        }

        const element = document.getElementById(`share-question-${question.id}`);
        if (!element) return;

        const { width, height } = element.getBoundingClientRect();
        const aspectRatio = width / height;
        const canvasWidth = 400;
        const canvasHeight = canvasWidth / aspectRatio;

        try {
            const dataUrl = await toPng(element, { quality: 2, canvasWidth, canvasHeight });
            const blob = await fetch(dataUrl).then(res => res.blob());

            const item = new window.ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);

            alert('Image copied to clipboard!');
        } catch (err) {
            console.error('Error copying to clipboard:', err);
        }
    };

    const emojis = ["❤️", "🙏", "🔥", "👍"];
    const localized = getLocalizedText();

    return (
        <>
            {/* Overlay with blur effect */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0, 0, 0, 0.6)",
                    backdropFilter: "blur(8px)",
                    zIndex: 999,
                    animation: "fadeIn 0.3s ease"
                }}
                onClick={onClose}
            ></div>

            {/* SharePopup Modal */}
            <div
                style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                    background: darkMode ? "#2d2d2d" : "#ffffff",
                    color: darkMode ? "#ffffff" : "#333",
                    padding: window.innerWidth <= 768 ? "16px" : "24px",
                    borderRadius: "24px",
                    boxShadow: darkMode
                        ? "0 20px 40px rgba(0, 0, 0, 0.5)"
                        : "0 20px 40px rgba(0, 0, 0, 0.15)",
                    maxWidth: "90%",
                    width: "700px",
                    maxHeight: "90%",
                    overflowY: "auto",
                    animation: "slideUp 0.3s ease"
                }}
            >
                {/* Header with language indicator */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "15px",
                    borderBottom: darkMode ? "1px solid #444" : "1px solid #e0e0e0"
                }}>
                    <h3 style={{
                        fontSize: window.innerWidth <= 768 ? "18px" : "20px",
                        fontWeight: "600",
                        color: darkMode ? "#ffffff" : "#333",
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                    }}>
                        📤 Share Question
                        {question.questionHi && (
                            <span style={{
                                fontSize: "12px",
                                backgroundColor: "#28a745",
                                color: "white",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontWeight: "normal"
                            }}>
                                {getLanguageDisplay()}
                            </span>
                        )}
                    </h3>
                </div>

                {/* Main content container for download */}
                <div
                    id={`share-question-${question.id}`}
                    style={{
                        background: darkMode ? "#2d2d2d" : "#ffffff",
                        borderRadius: "20px",
                        overflow: "hidden",
                        border: darkMode ? "1px solid #444" : "1px solid #e0e0e0",
                    }}
                >
                    {/* Question section */}
                    <div style={{
                        padding: "24px",
                        background: darkMode ? "#363636" : "#f8f9fa",
                        borderBottom: darkMode ? "1px solid #444" : "1px solid #e0e0e0"
                    }}>
                        <p style={{
                            fontSize: window.innerWidth <= 768 ? "16px" : "18px",
                            fontWeight: "500",
                            color: darkMode ? "#ffffff" : "#333",
                            lineHeight: "1.6",
                            margin: 0,
                            whiteSpace: "pre-line"
                        }}>
                            <span style={{ color: '#007bff', marginRight: '8px', fontWeight: '600' }}>Q:</span>
                            {localized.question}
                        </p>
                        {question.exam && (
                            <p style={{
                                fontSize: "12px",
                                color: darkMode ? "#999" : "#666",
                                marginTop: "8px",
                                fontStyle: "italic"
                            }}>
                                {question.exam}
                            </p>
                        )}
                    </div>

                    {/* Options section */}
                    <div style={{
                        padding: "24px",
                        background: darkMode ? "#2d2d2d" : "#ffffff"
                    }}>
                        <div style={{ marginTop: "8px" }}>
                            {localized.options.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleOptionClick(question.id, option)}
                                    style={{
                                        padding: window.innerWidth <= 768 ? "12px 16px" : "14px 20px",
                                        margin: "8px 0",
                                        cursor: "pointer",
                                        borderRadius: "12px",
                                        fontSize: window.innerWidth <= 768 ? "14px" : "16px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        ...getOptionStyle(selectedOption, question.correctAnswer, option),
                                    }}
                                >
                                    <span>{option}</span>
                                    <span style={{ fontSize: "18px" }}>
                                        {emojis[index]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer with copyright */}
                    <div style={{
                        padding: "15px 20px",
                        background: darkMode ? "#363636" : "#f8f9fa",
                        borderTop: darkMode ? "1px solid #444" : "1px solid #e0e0e0",
                        textAlign: "center"
                    }}>
                        <p style={{
                            fontSize: "13px",
                            color: darkMode ? "#999" : "#666",
                            margin: 0
                        }}>
                            © SSCVIDYA.IN
                        </p>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "20px",
                    flexWrap: "wrap",
                    justifyContent: "flex-end"
                }}>
                    <button
                        onClick={handleDownload}
                        style={{
                            padding: window.innerWidth <= 768 ? '10px 20px' : '12px 24px',
                            borderRadius: '30px',
                            border: 'none',
                            backgroundColor: '#28a745',
                            color: 'white',
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(40,167,69,0.3)'
                        }}
                    >
                        📥 Download
                    </button>
                    <button
                        onClick={handleCopyToClipboard}
                        style={{
                            padding: window.innerWidth <= 768 ? '10px 20px' : '12px 24px',
                            borderRadius: '30px',
                            border: 'none',
                            backgroundColor: '#007bff',
                            color: 'white',
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(0,123,255,0.3)'
                        }}
                    >
                        📋 Copy
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: window.innerWidth <= 768 ? '10px 20px' : '12px 24px',
                            borderRadius: '30px',
                            border: 'none',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(108,117,125,0.3)'
                        }}
                    >
                        ✕ Close
                    </button>
                </div>
            </div>

            {/* Add animation styles */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translate(-50%, -40%);
                    }
                    to {
                        opacity: 1;
                        transform: translate(-50%, -50%);
                    }
                }
            `}</style>
        </>
    );
};

export default SharePopup;

//OLDER VERSION
// import React, { useContext, useState } from "react";
// import { toPng } from "html-to-image";
// import { saveAs } from "file-saver";
// import { themeContext } from "../Context";

// const SharePopup = ({ question, onClose }) => {
//     const theme = useContext(themeContext);
//     const darkMode = theme.state.darkMode;

//     // Prevent scrolling on the background
//     React.useEffect(() => {
//         document.body.style.overflow = "hidden";
//         return () => {
//             document.body.style.overflow = "auto";
//         };
//     }, []);

//     //correct/incorrect answer highlighting feature

//     const [selectedOption, setSelectedOption] = useState(null);

//     const handleOptionClick = (questionId, option) => {
//         setSelectedOption(option);
//     };

//     const getOptionStyle = (selectedOption, correctAnswer, option) => {
//         if (option === correctAnswer && selectedOption === option) {
//             return { backgroundColor: 'lightgreen', color: 'black' }; // Correctly selected
//         }
//         if (option === selectedOption && option !== correctAnswer) {
//             return { backgroundColor: 'lightcoral', color: 'black' }; // Incorrectly selected
//         }
//         return {}; // Default style
//     };

//     // const handleDownload = () => {
//     //     const element = document.getElementById(`share-question-${question.id}`);
//     //     toPng(element, {
//     //         quality: 1,
//     //         canvasWidth: 644,
//     //         canvasHeight: 342,
//     //     })
//     //         .then((dataUrl) => {
//     //             saveAs(dataUrl, `question-${question.id}.png`);
//     //         })
//     //         .catch((err) => console.error("Error generating image:", err));
//     // };

//     const handleDownload = () => {
//         const element = document.getElementById(`share-question-${question.id}`);
//         if (!element) {
//             console.error("Element not found");
//             return;
//         }

//         const { width, height } = element.getBoundingClientRect(); // Get element's current dimensions
//         const aspectRatio = width / height;

//         // Adjust canvas size proportionally
//         const canvasWidth = 300; // Example base width
//         const canvasHeight = canvasWidth / aspectRatio;

//         toPng(element, {
//             quality: 1,
//             canvasWidth,
//             canvasHeight,
//         })
//             .then((dataUrl) => {
//                 saveAs(dataUrl, `question-${question.id}.png`);
//             })
//             .catch((err) => console.error("Error generating image:", err));
//     };

//     // Handle copy to clipboard functionality
//     const handleCopyToClipboard = async () => {
//         if (!navigator.clipboard || !window.ClipboardItem) {
//             console.error("Clipboard API not supported in this browser.");
//             alert("Clipboard API is not supported.");
//             return;
//         }

//         const element = document.getElementById(`share-question-${question.id}`);
//         if (!element) return;

//         const { width, height } = element.getBoundingClientRect(); // Get element's current dimensions
//         const aspectRatio = width / height;

//         // Adjust canvas size proportionally
//         const canvasWidth = 300; // Example base width
//         const canvasHeight = canvasWidth / aspectRatio;

//         try {
//             const dataUrl = await toPng(element, { quality: 1, canvasWidth, canvasHeight });
//             const blob = await fetch(dataUrl).then(res => res.blob());

//             const item = new window.ClipboardItem({ 'image/png': blob });
//             await navigator.clipboard.write([item]);

//             alert('Image copied to clipboard!');
//         } catch (err) {
//             console.error('Error copying to clipboard:', err);
//         }
//     };

//     const emojis = ["❤️", "🙏", "🔥", "👍"];

//     return (
//         <>
//             {/* Overlay for background blur and disabling interaction */}
//             <div
//                 style={{
//                     position: "fixed",
//                     top: 0,
//                     left: 0,
//                     width: "100%",
//                     height: "100%",
//                     background: "rgba(0, 0, 0, 0.5)", // Semi-transparent overlay
//                     backdropFilter: "blur(5px)", // Blur effect
//                     zIndex: 999, // Ensure it's below the popup
//                 }}
//             ></div>
//             {/* SharePopup Modal*/}
//             <div
//                 style={{
//                     position: "fixed",
//                     top: "50%",
//                     left: "50%",
//                     transform: "translate(-50%, -50%)",
//                     zIndex: 1000,
//                     background: darkMode ? "#222" : "white",
//                     color: darkMode ? "white" : "black",
//                     padding: "20px",
//                     borderRadius: "10px",
//                     boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
//                     maxWidth: "90%",
//                     width: "600px",
//                     maxHeight: "90%", // Constrain modal height
//                     overflowY: "auto", // Enable vertical scrolling
//                 }}
//             >
//                 <h3>Share or Download</h3>
//                 <div
//                     id={`share-question-${question.id}`}
//                     style={{
//                         background: darkMode ? "#333" : "white",
//                         color: darkMode ? "white" : "black",
//                         border: darkMode ? "2px solid #555" : "2px solid #000",
//                         borderRadius: "10px",
//                         padding: "20px",
//                         margin: "0 auto",
//                         textAlign: "center",
//                     }}
//                 >
//                     <div style={{
//                         marginBottom: "20px", border: darkMode ? "1px solid #555" : "1px solid #000", borderRadius: "10px", padding: "20px"
//                     }}>
//                         <h2 style={{ fontSize: "20px", marginBottom: "10px" }}>
//                             {question.question}
//                             <span style={{ fontWeight: "bold" }}> {question?.exam}</span>
//                         </h2>
//                         <ul style={{ listStyleType: "none", padding: 0 }}>
//                             {question.options.map((option, index) => (
//                                 <li
//                                     key={index}
//                                     onClick={() => handleOptionClick(question.id, option)}
//                                     style={{
//                                         padding: "10px",
//                                         margin: "5px 0",
//                                         cursor: "pointer",
//                                         border: darkMode ? "1px solid #666" : "1px solid #ddd",
//                                         borderRadius: "5px",
//                                         ...getOptionStyle(selectedOption, question.correctAnswer, option),
//                                     }}
//                                 >
//                                     {option}
//                                     <span style={{ marginLeft: "8px" }}>
//                                         {emojis[index]}
//                                     </span>
//                                 </li>
//                             ))}
//                         </ul>
//                     </div>

//                     <footer
//                         style={{
//                             position: "relative",
//                             bottom: "10px",
//                             background: darkMode ? "#555" : "#f1f1f1",
//                             color: darkMode ? "white" : "black",
//                             padding: "5px 10px",
//                             borderRadius: "5px",
//                         }}
//                     >
//                         <p style={{ fontSize: "14px", margin: 0 }}>© 2024 SSCVIDYA.IN</p>
//                     </footer>
//                     {/* Watermark */}
//                     {/* <div
//                         style={{
//                             position: "absolute",
//                             top: "50%",
//                             left: "50%",
//                             transform: "translate(-50%, -50%)",
//                             fontSize: "18px",
//                             color: "rgba(0, 0, 0, 0.1)", // Faded color
//                             fontWeight: "bold",
//                             whiteSpace: "nowrap",
//                             pointerEvents: "none",
//                             userSelect: "none",
//                         }}
//                     >
//                         SSCVIDYA.IN
//                     </div> */}
//                 </div>
//                 <button
//                     onClick={handleDownload}
//                     className="button"
//                     style={{ marginTop: "10px" }}
//                 >
//                     Download
//                 </button>

//                 <button
//                     onClick={handleCopyToClipboard}
//                     className="button"
//                     style={{ marginTop: "10px", marginLeft: "10px" }}
//                 >
//                     Copy Clipboard
//                 </button>

//                 <button
//                     onClick={onClose}
//                     className="button"
//                     style={{ marginTop: "10px", marginLeft: "10px" }}
//                 >
//                     Close
//                 </button>
//             </div>
//         </>

//     );
// };

// export default SharePopup;
